<template>
    <div class="entity-administration-component-root">
        <slot name="page-header" :items="items"></slot>
        <div class="container-fluid p-0 mt-1 mb-3">
            <div class="row">
                <div class="d-flex justify-content-center justify-content-lg-start col-12 col-lg-4">
                    <button @click="addItem" type="button" class="btn btn-primary mb-3 mb-lg-0">{{ $t("common.add") }}</button>
                </div>
                <div class="d-flex justify-content-center col-12 col-lg-4">
                    <pagination v-if="totalCount > pageRowsCount"
                                v-model="currentPage"
                                :total-rows-count="totalCount"
                                :page-rows-count="pageRowsCount"
                                :showing-pages-count="5"></pagination>
                </div>
                <div class="d-flex justify-content-center justify-content-lg-end col-12 col-lg-4">
                    <label class="float-right mt-1">{{ $t('common.totalCount') }}: <span class="text-muted">{{ totalCount }}</span></label>
                </div>
            </div>
        </div>
        <div class="entity-administration-table-wrapper table-responsive">
            <table v-if="items" class="table table-bordered table-sm">
                <thead class="thead-light">
                    <tr>
                        <th v-for="(column, columnIndex) in tableColumns" :key="column.name"
                            @click.exact="setSortingBySingleColumn(columnIndex)"
                            @click.ctrl="addColumnForMulticolumnSorting(columnIndex)"
                            class="sorted-column align-middle">
                            <div class="d-flex justify-content-between align-items-center">
                                <div>{{ column.displayName }}</div>
                                <div class="ml-1 d-flex align-items-center">
                                    <div class="d-flex flex-column">
                                        <i :class="getIconClass(columnIndex)"></i>
                                    </div>
                                    <div v-if="sortings.length > 1 && getSortNumber(columnIndex) > 0" class="text-primary ml-1">{{ getSortNumber(columnIndex) }}</div>
                                </div>
                            </div>
                        </th>
                        <th class="col-edit align-middle">{{ $t('common.actions') }}</th>
                    </tr>
                    <tr>
                        <th v-for="(column, columnIndex) in tableColumns" :key="column.name" class="p-0 align-middle">
                            <field-filter ref="filterRef"
                                          class="m-1"
                                          v-model="filters[columnIndex]"
                                          :key="columnIndex"
                                          :index="columnIndex"
                                          :field-name="column.displayName"
                                          :column-name="column.name"
                                          :entity-name="entityName"
                                          :max-length="column.maxLength"
                                          :type="column.typeName"
                                          :show-null-checkbox="hasNullableColumn"
                                          :is-url="column.isUrl"
                                          :is-nullable="column.isNullable"
                                          :is-multiline="column.isMultiline"
                                          :is-date-only="column.isDateOnly"
                                          :select-values="column.selectValues"
                                          :is-foreign-key="column.isForeignKey"
                                          :display-name="getDisplayName(currentItem, column, '')">
                            </field-filter>
                        </th>
                        <th class="align-middle">
                            <i v-if="isFilters" :title="$t('common.clearFilters')" @click="resetFilters" class="fa fa-times fa-lg d-flex justify-content-center"></i>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="item in items">
                        <td v-for="column in tableColumns">
                            <template v-if="!column.isUrl">
                                <template v-if="column.isMultiline">
                                    <pre>{{ getColumnValue(item, column) }}</pre>
                                </template>
                                <template v-else>
                                    {{ getColumnValue(item, column) }}
                                </template>
                            </template>
                            <template v-else>
                                <a target="_blank" :href="getColumnValue(item, column)">{{ getColumnValue(item, column) }}</a>
                            </template>
                        </td>
                        <td class="col-edit">
                            <div class="align-content-center">
                                <i :title="$t('common.edit')" @click="editItem(item)" class="fa fa-edit mr-2"></i>
                                <i :title="$t('common.delete')" @click="deleteItem(item)" class="fa fa-trash"></i>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <modal v-if="info && showModal" @shown="isModalShown = true" @hidden="isModalShown = false" ref="editItemsDialog" :title="title">
            <template slot="body">
                <form v-if="currentItem">
                    <div class="form-row">
                        <edit-field v-for="(column, columnIndex) in writableColumns"
                                    ref="inputRef"
                                    v-model="currentItem[column.name]"
                                    :key="columnIndex"
                                    :index="columnIndex"
                                    :field-name="column.displayName"
                                    :column-name="column.name"
                                    :entity-name="entityName"
                                    :max-length="column.maxLength"
                                    :type="column.typeName"
                                    :is-url="column.isUrl"
                                    :is-nullable="column.isNullable"
                                    :is-multiline="column.isMultiline"
                                    :is-date-only="column.isDateOnly"
                                    :select-values="column.selectValues"
                                    :is-foreign-key="column.isForeignKey"
                                    :display-name="getDisplayName(currentItem, column, '')"
                                    :is-modal-shown="isModalShown">
                        </edit-field>
                    </div>
                </form>
            </template>
            <template v-slot:okButton="{ close }">
                <button type="button" @click="save(close)" class="btn btn-primary">{{ $t("common.ok") }}</button>
            </template>
        </modal>

        <confirm-dialog></confirm-dialog>
    </div>
</template>

<script>
import "@/vue";
import i18n from "@/vue/localization";
import toastr from "toastr";
import EditFieldComponent from "./EditField.vue";
import FieldFilterComponent from "./FieldFilter.vue";
import EntityAdministrationService from "./entityAdministrationService";
import Pagination from "@/common/Pagination.vue";

import _ from "lodash";
import dateFormatter from "@/common/dateFormatter.js";

const EntityAdministrationComponent = {
    i18n: i18n,

    props: ["entityName", "addButtonText", "CreateDialogTitle", "EditDialogTitle"],

    data() {
        return {
            service: new EntityAdministrationService(),
            items: null,
            info: null,
            blankItem: null,
            currentItem: null,
            writableColumns: [],
            tableColumns: [],
            primaryKey: null,
            isNew: false,
            showModal: false,

            totalCount: 0,
            currentPage: 1,
            pageRowsCount: 100,

            sortings: [],
            lastSortDirection: null,
            lastSortColumn: null,

            filters: [],
            lastSimplifiedFilters: [],

            isModalShown: false
        };
    },

    components: {
        "edit-field": EditFieldComponent,
        "field-filter": FieldFilterComponent,
        "pagination": Pagination
    },

    computed: {
        title() {
            return this.isNew ? this.$t("common.add") : this.$t("common.edit");
        },

        simplifiedFilters() {
            if (!this.filters) {
                return [];
            }

            const filters = this.filters.map(f => {
                if (f === null) {
                    return null;
                }

                if (f.selectedValue === null && f.selectNull === false) {
                    return null;
                }

                return f;
            });

            if (filters.every(f => f === null)) {
                return [];
            }

            return filters;
        },
        lastFiltersCount() {
            return this.lastSimplifiedFilters.filter(f => f !== null).length;
        },
        filtersCount() {
            return this.simplifiedFilters.filter(f => f !== null).length;
        },
        isFilters() {
            return this.filtersCount > 0;
        },
        changeFilters() {
            if (this.filtersCount !== this.lastFiltersCount) {
                return true;
            }

            const last = this.lastSimplifiedFilters;
            const current = this.simplifiedFilters;

            if (current.length !== last.length) {
                return true;
            }

            for (let i = 0; i < current.length; i++) {
                if (!current[i] && !last[i]) {
                    continue;
                }

                if (!!current[i] !== !!last[i]) {
                    return true;
                }
                if (current[i].selectedValue !== last[i].selectedValue) {
                    return true;
                }
                if (current[i].selectNull !== last[i].selectNull) {
                    return true;
                }
            }

            return false;
        },

        hasNullableColumn() {
            return this.tableColumns.filter(tc => tc.isNullable).length > 0;
        }
    },

    watch: {
        currentPage() {
            this.debouncedLoadItems();
        },
        lastSortDirection() {
            this.debouncedLoadItems();
        },
        lastSortColumn() {
            this.debouncedLoadItems();
        },
        changeFilters(newValue) {
            if (newValue) {
                this.setFilters();
            }
        }
    },

    created() {
        this.debouncedLoadItems = _.debounce(this.loadItems, 500);
        this.loadItems();
    },

    methods: {
        setFilters() {
            this.lastSimplifiedFilters = this.simplifiedFilters.slice();
            this.debouncedLoadItems();
        },
        resetFilters() {
            this.filters = [];
            this.debouncedLoadItems();
            this.lastSimplifiedFilters = this.simplifiedFilters.slice();
        },
        getFilterRequests() {
            return this.filters
                .map((f, index) => {
                    if (!f || (f.selectNull === false && f.selectedValue === null)) {
                        return null;
                    }

                    return {
                        name: this.tableColumns[index].name,
                        value: f.selectNull ? null : f.selectedValue
                    };
                })
                .filter(f => f);
        },

        getSortNumber(columnIndex) {
            return _.findIndex(this.sortings, s => s.columnIndex === columnIndex) + 1;
        },
        getSortRequests() {
            return this.sortings.filter(s => s.sortDirection !== "None").map(s => {
                return {
                    fieldName: this.tableColumns[s.columnIndex].name,
                    direction: s.sortDirection === "Ascending" ? 0 : 1
                };
            });
        },
        setLastSorting() {
            if (this.sortings.length === 0) {
                this.lastSortDirection = null;
                this.lastSortColumn = null;
            } else {
                const lastSorting = this.sortings[this.sortings.length - 1];
                this.lastSortDirection = lastSorting.sortDirection;
                this.lastSortColumn = lastSorting.columnIndex;
            }
        },
        setSortingBySingleColumn(columnIndex) {
            if (this.sortings.length === 0 || this.sortings.length > 1 || this.sortings[0].columnIndex !== columnIndex) {
                this.sortings = [
                    {
                        columnIndex: columnIndex,
                        sortDirection: "Ascending"
                    }
                ];

                this.setLastSorting();
                return;
            }

            this.sortings[0].sortDirection = (this.sortings[0].sortDirection === "Ascending") ? "Descending" : "Ascending";
            this.setLastSorting();
        },
        addColumnForMulticolumnSorting(columnIndex) {
            if (this.sortings.length === 0) {
                this.sortings = [
                    {
                        columnIndex: columnIndex,
                        sortDirection: "Ascending"
                    }
                ];

                this.setLastSorting();
                return;
            }

            let lastSorting = this.sortings[this.sortings.length - 1];

            if (this.sortings.length === 1 && lastSorting.columnIndex === columnIndex) {
                lastSorting.sortDirection = (lastSorting.sortDirection === "Ascending") ? "Descending" : "Ascending";
                this.setLastSorting();
                return;
            }

            if (lastSorting.columnIndex === columnIndex) {
                if (lastSorting.sortDirection === "Ascending") {
                    lastSorting.sortDirection = "Descending";
                } else {
                    this.sortings.pop();
                }

                this.setLastSorting();
                return;
            }

            if (_.find(this.sortings, s => s.columnIndex === columnIndex)) {
                return;
            }

            this.sortings.push({
                columnIndex: columnIndex,
                sortDirection: "Ascending"
            });
            this.setLastSorting();
        },
        getSortDirectionForColumn(columnIndex) {
            if (this.sortings.length === 0) {
                return "None";
            }

            const sorting = _.find(this.sortings, s => s.columnIndex === columnIndex);
            if (sorting) {
                return sorting.sortDirection;
            }

            return "None";
        },

        getIconClass(columnIndex) {
            if (this.getSortDirectionForColumn(columnIndex) === "Ascending") {
                return "fa fa-sort-asc text-primary mt-1";
            }
            if (this.getSortDirectionForColumn(columnIndex) === "Descending") {
                return "fa fa-sort-desc text-primary mb-1";
            }

            return "fa fa-sort text-secondary";
        },

        getColumnValue(item, column) {
            const displayName = this.getDisplayName(item, column, "id ");

            if (displayName) {
                return displayName;
            }

            if (column.typeName === "date") {
                if (!item[column.name]) {
                    return "-";
                }

                return column.isDateOnly ? this.formatDate(item[column.name]) : this.formatDateTime(item[column.name]);
            }

            if (column.typeName === "select") {
                if (!column.selectValues[item[column.name]]) {
                    return "-";
                }

                return column.selectValues[item[column.name]].displayName;
            }

            if (column.typeName !== "bool") {
                return item[column.name];
            }

            if (item[column.name] === true) {
                return this.$t("common.yes");
            }

            if (item[column.name] === false) {
                return this.$t("common.no");
            }

            return "-";
        },

        getDisplayName(item, column, prefix) {
            if (column.isForeignKey) {
                const propName = "_foreignKeyTarget_" + column.name;
                const displayName = item[propName];

                if (displayName) {
                    return displayName;
                }

                return prefix + item[column.name];
            }

            return "";
        },

        loadItems() {
            const request = {
                entityName: this.entityName,
                skip: this.pageRowsCount * (this.currentPage - 1),
                take: this.pageRowsCount,
                sortings: this.getSortRequests(),
                filters: this.getFilterRequests()
            };

            this.service.getObjects(request)
                .then(result => {
                    this.info = result.info;
                    this.tableColumns = result.info.columnsInfo.filter(c => c.showInTable);
                    this.writableColumns = result.info.columnsInfo.filter(c => c.isWritable && c.name !== result.info.primaryKey)
                    this.items = result.items;
                    this.totalCount = result.totalCount;

                    this.primaryKey = result.info.primaryKey;

                    if (this.sortings.length === 0 && this.info.defaultSortings) {
                        this.sortings = this.info.defaultSortings.map(s => {
                            return {
                                columnIndex: _.findIndex(this.tableColumns, c => c.name === s.fieldName),
                                sortDirection: s.direction === 0 ? "Ascending" : "Descending"
                            };
                        });
                    }

                    var currentItem = {};
                    result.info.columnsInfo.forEach(c => currentItem[c.name] = this.getDefaultValue(c.typeName, c.isNullable || c.name === result.info.primaryKey, c.isForeignKey));
                    this.currentItem = currentItem;
                    this.blankItem = Object.assign({}, this.currentItem);
                });
        },

        editItem(item) {
            this.isNew = false;
            this.currentItem = Object.assign({}, item);
            this.showModal = true;
            this.openModal();
        },

        deleteItem(item) {
            const message = this.$t("common.deleteItem");

            this.$confirm({ body: message, title: this.$t("common.confirmDelete") }).then(res => {
                if (!res) {
                    return;
                }

                const loader = this.$loading.show();

                const request = {
                    id: item[this.primaryKey],
                    entityName: this.entityName
                };

                this.service.deleteObject(request)
                    .fail(response => {
                        loader.hide();
                        toastr.error(response.responseJSON.message);
                    })
                    .then(response => {
                        loader.hide();
                        if (response.success) {
                            toastr.success(this.$t("common.dataSuccessfullyDeleted"));
                            this.resetValidation();
                            this.loadItems();
                        } else {
                            toastr.error(response.message);
                        }
                    });
            });
        },

        addItem() {
            this.isNew = true;
            this.showModal = true;
            this.currentItem = Object.assign({}, this.blankItem);
            this.openModal();
        },

        openModal() {
            this.$nextTick(() => {
                this.$refs.editItemsDialog.show().done(() => {
                    this.resetValidation();
                    this.showModal = false;
                });
            });
        },

        save(hideModal) {
            this.validate().then(res => {
                if (!res || res.filter(r => !r).length > 0) {
                    return;
                }

                Object.keys(this.currentItem).forEach(p => {
                    if (this.currentItem[p] === "") {
                        this.currentItem[p] = null;
                    }
                });

                const loader = this.$loading.show();

                const request = {
                    request: this.currentItem,
                    entity: this.entityName
                };

                this.service.createEditObject(request)
                    .fail(response => {
                        loader.hide();
                        toastr.error(response.responseJSON.message)
                    })
                    .then(response => {
                        loader.hide();

                        if (!response.success) {
                            toastr.error(response.message);
                            return;
                        }

                        toastr.success(this.$t("common.dataSuccessfullySaved"));
                        hideModal();
                        this.showModal = false;
                        this.resetValidation();
                        this.loadItems();
                    });
            });
        },

        validate() {
            return Promise.all(this.$refs.inputRef.map(r => r.$validator.validateAll()));
        },

        resetValidation() {
            const inputRef = this.$refs.inputRef;

            if (inputRef) {
                this.$refs.inputRef.map(r => r.$validator.reset());
            }
        },

        getDefaultValue(type, isNullable, isForeignKey) {
            if (isNullable || isForeignKey) {
                return null;
            }

            if (type === "date") {
                return new Date();
            }

            if (type === "number") {
                return 0;
            }

            if (type === "bool") {
                return false;
            }

            if (type === "select") {
                return 0;
            }

            if (type === "string") {
                return "";
            }

            return null;
        },

        formatDateTime: dateFormatter.formatDateTime,

        formatDate: dateFormatter.formatDate
    }
};

export default EntityAdministrationComponent;
</script>
